import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  Lock,
  CheckCircle,
  ShieldCheck,
  MapPin,
  Server,
  Eye,
  KeyRound,
  FileCheck,
  Globe,
  Database,
  ArrowRight,
  Mail,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { PublicHeader } from '../components/PublicHeader';
import { useForceLightMode } from '../hooks/useForceLightMode';

export const SecurityPage: React.FC = () => {
  useForceLightMode();

  const sections = [
    {
      icon: Server,
      iconColor: 'text-cyan-600',
      iconBg: 'from-cyan-50 to-cyan-100',
      title: 'Infrastructure & Hosting',
      items: [
        'Hosted entirely on Amazon Web Services (AWS) — one of the world\'s most trusted cloud platforms',
        'All data stored in UK data centres — AI processing remains within secure AWS infrastructure',
        'Databases isolated in a private network — not accessible from the public internet',
        'Automatic daily backups with 7-day retention and recovery options',
        'All infrastructure changes are version-controlled and deployed through automated pipelines',
      ],
    },
    {
      icon: Lock,
      iconColor: 'text-green-600',
      iconBg: 'from-green-50 to-green-100',
      title: 'Encryption',
      items: [
        'All data encrypted in transit — every connection uses HTTPS',
        'All data encrypted at rest using industry-standard AES-256 encryption',
        'Database connections are always encrypted',
        'Uploaded documents are encrypted automatically when stored',
      ],
    },
    {
      icon: KeyRound,
      iconColor: 'text-purple-600',
      iconBg: 'from-purple-50 to-purple-100',
      title: 'Authentication & Access Control',
      items: [
        'Multi-factor authentication — authenticator app, email, SMS, and backup codes',
        'Passwords are securely encrypted — never stored as plain text',
        'Granular permissions — control who can access each case',
        'Automated protection against unauthorised login attempts',
        'Secure session handling — you\'re automatically signed out after inactivity',
      ],
    },
    {
      icon: Eye,
      iconColor: 'text-amber-600',
      iconBg: 'from-amber-50 to-amber-100',
      title: 'Audit & Monitoring',
      items: [
        'Complete audit trail — every action is recorded with who did it, when, and from where',
        'Real-time monitoring — we\'re alerted immediately if something goes wrong',
        'All logs stored centrally for rapid incident investigation',
        'Automated security scanning to catch vulnerabilities early',
      ],
    },
    {
      icon: FileCheck,
      iconColor: 'text-teal-600',
      iconBg: 'from-teal-50 to-teal-100',
      title: 'Compliance & Standards',
      items: [
        'NHS Data Security and Protection Toolkit (DSPT) — completed and published',
        'Fully GDPR compliant with documented data processing agreements',
        'Registered with the Information Commissioner\'s Office (ICO)',
        'Data Processing Agreements available for all customers on request',
        'Documented incident response plan — we know what to do if something goes wrong',
      ],
    },
    {
      icon: Database,
      iconColor: 'text-rose-600',
      iconBg: 'from-rose-50 to-rose-100',
      title: 'AI & Data Processing',
      items: [
        'AI processing runs on AWS Bedrock — your data never leaves AWS and is never stored or seen by the AI provider',
        'No patient or case data is used to train AI models',
        'AI outputs are clearly labelled and positioned as assistive, not diagnostic',
        'Document text extraction runs entirely within our own infrastructure',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30">
      <PublicHeader />

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-medical-900 mb-6 leading-tight tracking-tight">
            Your data security is our foundation
          </h1>
          <p className="text-xl text-medical-600 leading-relaxed font-light">
            ClinLexis is built from the ground up for medico-legal professionals who handle sensitive
            patient data. Every decision we make prioritises security, compliance, and keeping your data in the UK.
          </p>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-20">
          {[
            { icon: ShieldCheck, label: 'NHS DSPT Compliant' },
            { icon: Shield, label: 'GDPR Compliant' },
            { icon: MapPin, label: 'UK Data Storage' },
            { icon: Lock, label: 'Fully Encrypted' },
            { icon: Globe, label: 'ICO Registered' },
          ].map((badge, index) => (
            <div key={index} className="flex items-center space-x-2 px-4 py-2.5 bg-white border border-medical-200/60 rounded-full shadow-sm">
              <badge.icon className="h-4 w-4 text-primary-600" />
              <span className="text-sm font-medium text-medical-700">{badge.label}</span>
            </div>
          ))}
        </div>

        {/* Security sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {sections.map((section, index) => (
            <div key={index} className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 border border-medical-200/50">
              <div className="flex items-center space-x-3 mb-6">
                <div className={`w-12 h-12 bg-gradient-to-br ${section.iconBg} rounded-xl flex items-center justify-center`}>
                  <section.icon className={`h-6 w-6 ${section.iconColor}`} />
                </div>
                <h2 className="text-xl font-semibold text-medical-900">{section.title}</h2>
              </div>
              <ul className="space-y-3 text-medical-600">
                {section.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <CheckCircle className={`h-4 w-4 ${section.iconColor} flex-shrink-0 mt-0.5`} />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* AWS inherited certifications callout */}
        <div className="bg-gradient-to-br from-medical-800 to-medical-900 rounded-2xl p-10 md:p-12 text-white mb-20">
          <div className="max-w-3xl mx-auto text-center">
            <Server className="h-10 w-10 text-primary-300 mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold mb-4 tracking-tight">
              Built on AWS — enterprise-grade infrastructure
            </h2>
            <p className="text-medical-300 mb-8 leading-relaxed">
              By hosting exclusively on Amazon Web Services, your data benefits from the same
              security standards trusted by banks, governments, and the NHS.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['SOC 1/2/3', 'ISO 27001', 'ISO 27017', 'Cyber Essentials Plus'].map((cert) => (
                <div key={cert} className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm font-medium">
                  {cert}
                </div>
              ))}
            </div>
            <p className="text-xs text-medical-400 mt-4">
              These certifications apply to the AWS infrastructure that hosts ClinLexis.
            </p>
          </div>
        </div>

        {/* Responsible disclosure & contact */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-medical-900 mb-4 tracking-tight">
            Questions about our security?
          </h2>
          <p className="text-medical-600 mb-8 leading-relaxed">
            We're happy to walk you through our security measures, share our DSPT evidence, or
            provide a Data Processing Agreement. If you believe you've found a security issue,
            please let us know.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="btn-primary px-8 py-3.5 text-base flex items-center space-x-2 group shadow-lg shadow-primary-200/50 hover:shadow-xl transition-all"
            >
              <Mail className="h-5 w-5" />
              <span>Get in touch</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="mailto:contact@clinlexis.co.uk"
              className="text-medical-700 hover:text-medical-900 font-semibold text-base px-6 py-3.5 border border-medical-300 rounded-lg hover:border-medical-400 transition-all"
            >
              contact@clinlexis.co.uk
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-medical-200 mt-16 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-2">
          <p className="text-sm text-medical-400 text-center">
            &copy; {new Date().getFullYear()} ClinLexis&trade;. All rights reserved.
          </p>
          <p className="text-xs text-medical-500 text-center">
            ClinLexis Ltd &middot; Company No. 17081483 &middot; Registered in England and Wales &middot; 238a Telegraph Road, Heswall, Wirral, CH60 0AL
          </p>
        </div>
      </footer>
    </div>
  );
};
